<template>
  <div class="library__article">
    <h1 class="library__h1">
      <VisibleText>Terms of Use for ResearchAI</VisibleText>
    </h1>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        These Terms of Use explain how organizations and their authorized users may access and use
        ResearchAI. We keep our terms simple and readable because trust comes from clarity. By using
        ResearchAI, you agree to follow these Terms on behalf of your organization.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Purpose of These Terms</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        ResearchAI is a Business-to-Business (B2B) and Business-to-Government (B2G) service built on
        a privacy‑first and validation‑first architecture. These Terms describe the responsibilities
        of organizations and users, and outline how we maintain a safe, secure, and trustworthy
        environment for all Customers.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Who May Use ResearchAI</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        Access to ResearchAI is provided to organizations and the users they authorize. Your
        organization is responsible for ensuring that all users:
      </VisibleText>
    </p>

    <ul class="library__list">
      <li><VisibleText>act on behalf of the organization</VisibleText></li>
      <li><VisibleText>use the platform lawfully and professionally</VisibleText></li>
      <li><VisibleText>follow these Terms and internal organizational policies</VisibleText></li>
    </ul>

    <p>
      <VisibleText>
        We may suspend or remove accounts that violate these expectations to protect other users and
        the integrity of the platform.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Your Content and Responsibilities</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        You retain ownership of all content you upload to ResearchAI. To maintain a safe and
        productive environment, you agree not to upload:
      </VisibleText>
    </p>

    <ul class="library__list">
      <li><VisibleText>unlawful, abusive, or discriminatory material</VisibleText></li>
      <li><VisibleText>malicious code, malware, or attempts to bypass security</VisibleText></li>
      <li>
        <VisibleText
          >content you do not have the right to use, including proprietary code</VisibleText
        >
      </li>
      <li>
        <VisibleText>personal data that violates your legal or contractual obligations</VisibleText>
      </li>
    </ul>

    <p>
      <VisibleText>
        We may remove content or suspend accounts that put other users, systems, or your own
        organization at risk.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>How We Handle Your Data</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        ResearchAI follows strict data minimization principles and does not use customer data to
        train external models. We do not sell or share customer data with third parties for
        advertising. Data is processed only to provide the service, improve reliability, and ensure
        security.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        For full details, please see our
        <router-link to="/library/privacy-policy"> Privacy</router-link> &
        <router-link to="/library/ethics-policy"> Ethics</router-link> Policy.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Acceptable Use</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        To maintain a secure and trustworthy environment, you agree not to:
      </VisibleText>
    </p>

    <ul class="library__list">
      <li><VisibleText>interfere with or disrupt the service</VisibleText></li>
      <li>
        <VisibleText
          >attempt to reverse‑engineer or extract source code without permission</VisibleText
        >
      </li>
      <li><VisibleText>use ResearchAI to generate harmful or deceptive outputs</VisibleText></li>

      <li><VisibleText>impersonate others or misrepresent your identity</VisibleText></li>
    </ul>

    <p>
      <VisibleText>
        We reserve the right to take action, including suspension, to protect the platform and its
        users.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Intellectual Property</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        ResearchAI’s software, models, and platform features are owned by ResearchAI. You may use
        the service as provided, but you may not copy, modify, redistribute, or attempt to extract
        proprietary components. Your organization retains full ownership of its own data, insights,
        and outputs.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Service Reliability and Changes</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        We aim for high availability and reliability. The service may evolve over time as we improve
        security, performance, and functionality. We will communicate material changes that affect
        your use of the platform.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Security</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        We use industry‑standard protections to safeguard your data. You agree to keep credentials
        secure, notify us of unauthorized access, and follow your organization’s security policies
        when using the platform.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Termination</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        You may stop using ResearchAI at any time. We may suspend or terminate access if these Terms
        are violated, if your use poses risk to others, or if required payments are not made. Upon
        termination, you may export your and or fully delete your data in accordance with your plan
        and our privacy policy.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Liability and Disclaimers</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        ResearchAI provides analytical tools and insights, but your organization is responsible for
        how those insights are used. To the extent permitted by law, the service is provided “as
        is,” and our total liability is limited to the amount paid for the service in the previous
        12 months.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Governing Law</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        These Terms are governed by the laws of the jurisdiction in which your organization is
        registered, unless otherwise agreed in writing.
      </VisibleText>
    </p>

    <h2 class="library__h2"><VisibleText>Contacting Us</VisibleText>:</h2>
    <div class="library__heading-line"></div>

    <p>
      <VisibleText>
        For questions about these Terms or to report misuse, contact us at privacy@researchai.io.
      </VisibleText>
    </p>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';
export default {
  name: 'TermsOfService',
  setup() {
    const siteData = reactive({
      title: "PigeonLine's ResearchAI | Terms of Service",
      description: "PigeonLine's ResearchAI's Terms of Service",
    });
    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });
  },
  methods: {
    goto(refName) {
      this.$refs[refName].scrollIntoView();
    },
  },
};
</script>
